import {
  AcquisitionDetails,
  ImageMetadata,
  TargetData,
  NightMetadata,
} from "../types";
import { targetsApi } from "./api";

export const normalizeTargetName = (name: string): string => {
  return name
    .replace(/\s+/g, "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase();
};

export const getLocationInfo = (
  lat: number,
  lon: number
): { name: string; bortle: number; elevation?: number } => {
  console.log("lat", lat);
  console.log("lon", lon);
  if (Math.abs(lat - 30) < 0.1 && Math.abs(lon - 34) < 0.1) {
    return { name: "Mahantush", bortle: 3 };
  } else if (Math.abs(lat - 32) < 0.1 && Math.abs(lon - 35) < 0.1) {
    return { name: "Home", bortle: 7 };
  }
  return { name: "Unknown", bortle: 0 };
};

export const normalizeFilterName = (filterName: string): string => {
  const normalized = filterName.toLowerCase();
  if (
    normalized === "none" ||
    normalized === "no-filter" ||
    normalized === "nofilter"
  ) {
    return "No-Filter";
  }
  return filterName;
};

export const determineCameraType = (cameraName: string): "OSC" | "Mono" => {
  const name = cameraName.toLowerCase();
  return name.includes("mm") ? "Mono" : "OSC";
};

export const processTargetData = (
  acquisitionDetails: AcquisitionDetails,
  images: ImageMetadata[]
): TargetData => {
  // Assign FrameType based on FilePath
  const imagesWithType = images.map((img) => {
    let frameType: "LIGHT" | "DARK" | "FLAT" | "BIAS" = "LIGHT";
    if (img.FilePath.match(/[/\\]FLAT[/\\]/i)) frameType = "FLAT";
    else if (img.FilePath.match(/[/\\]DARK[/\\]/i)) frameType = "DARK";
    else if (img.FilePath.match(/[/\\]BIAS[/\\]/i)) frameType = "BIAS";
    return { ...img, FrameType: frameType };
  });

  const totalExposureTime = imagesWithType.reduce(
    (sum, img) => sum + img.Duration,
    0
  );
  const uniqueFilters = Array.from(
    new Set(imagesWithType.map((img) => normalizeFilterName(img.FilterName)))
  );

  // Enhanced: Group images by night with fallback logic
  const nightGroups: Record<string, ImageMetadata[]> = {};
  let fallbackNight: string | null = null;
  imagesWithType.forEach((img, idx) => {
    // 1. Try standard pattern
    let match = img.FilePath.match(/DATE_(\d{4}-\d{2}-\d{2})/);
    let night = match ? match[1] : null;
    // 2. Try any date in the path
    if (!night) {
      match = img.FilePath.match(/(\d{4}-\d{2}-\d{2})/);
      night = match ? match[1] : null;
    }
    // 3. Fallback: use date from ExposureStart/ExposureStartUTC of first frame
    if (!night && idx === 0) {
      let dateStr = img.ExposureStart || img.ExposureStartUTC || "";
      // Try to extract date part (YYYY-MM-DD)
      const dateMatch = dateStr.match(/(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) {
        night = dateMatch[1];
      }
      fallbackNight = night;
    } else if (!night && fallbackNight) {
      night = fallbackNight;
    }
    // If still no night, skip grouping for this image
    if (!night) return;
    if (!nightGroups[night]) nightGroups[night] = [];
    nightGroups[night].push(img);
  });

  // If no nights were found at all, fallback: group all images under a single night from first frame
  if (Object.keys(nightGroups).length === 0 && imagesWithType.length > 0) {
    let fallback = null;
    // Try to extract from first frame's ExposureStart/ExposureStartUTC
    const first = imagesWithType[0];
    let dateStr = first.ExposureStart || first.ExposureStartUTC || "";
    const dateMatch = dateStr.match(/(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) {
      fallback = dateMatch[1];
    } else {
      fallback = "unknown-night";
    }
    nightGroups[fallback] = imagesWithType;
  }

  // Build per-night metadata
  const nightMetadata: Record<string, NightMetadata> = {};
  Object.keys(nightGroups).forEach((night) => {
    const loc = getLocationInfo(
      acquisitionDetails.ObserverLatitude,
      acquisitionDetails.ObserverLongitude
    );
    nightMetadata[night] = {
      date: night,
      acquisitionDetails: { ...acquisitionDetails },
      location: {
        name: loc.name,
        bortle: loc.bortle,
        elevation: loc.elevation ?? acquisitionDetails.ObserverElevation ?? 0,
      },
      cameraType: determineCameraType(acquisitionDetails.CameraName),
    };
  });

  // Extract equipment fields
  const equipment = {
    telescopeName: acquisitionDetails.TelescopeName,
    focalLength: acquisitionDetails.FocalLength,
    focalRatio: acquisitionDetails.FocalRatio,
    cameraName: acquisitionDetails.CameraName,
    pixelSize: acquisitionDetails.PixelSize,
    bitDepth: acquisitionDetails.BitDepth,
  };

  return {
    id: crypto.randomUUID(),
    name: acquisitionDetails.TargetName,
    equipment,
    nightMetadata,
    images: imagesWithType,
    totalExposureTime,
    filters: uniqueFilters,
  };
};

export const groupTargetsByName = (
  targets: TargetData[]
): Map<string, TargetData[]> => {
  const grouped = new Map<string, TargetData[]>();

  targets.forEach((target) => {
    const normalizedName = normalizeTargetName(target.name);
    const existing = grouped.get(normalizedName) || [];
    grouped.set(normalizedName, [...existing, target]);
  });

  return grouped;
};

export const groupImagesByNight = (
  images: ImageMetadata[]
): Record<string, ImageMetadata[]> => {
  const nightGroups: Record<string, ImageMetadata[]> = {};
  let fallbackNight: string | null = null;
  images.forEach((img, idx) => {
    // 1. Try standard pattern
    let match = img.FilePath.match(/DATE_(\d{4}-\d{2}-\d{2})/);
    let night = match ? match[1] : null;
    // 2. Try any date in the path
    if (!night) {
      match = img.FilePath.match(/(\d{4}-\d{2}-\d{2})/);
      night = match ? match[1] : null;
    }
    // 3. Fallback: use date from ExposureStart/ExposureStartUTC of first frame
    if (!night && idx === 0) {
      let dateStr = img.ExposureStart || img.ExposureStartUTC || "";
      const dateMatch = dateStr.match(/(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) {
        night = dateMatch[1];
      }
      fallbackNight = night;
    } else if (!night && fallbackNight) {
      night = fallbackNight;
    }
    if (!night) return;
    if (!nightGroups[night]) nightGroups[night] = [];
    nightGroups[night].push(img);
  });
  // If no nights were found at all, fallback: group all images under a single night from first frame
  if (Object.keys(nightGroups).length === 0 && images.length > 0) {
    let fallback = null;
    const first = images[0];
    let dateStr = first.ExposureStart || first.ExposureStartUTC || "";
    const dateMatch = dateStr.match(/(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) {
      fallback = dateMatch[1];
    } else {
      fallback = "unknown-night";
    }
    nightGroups[fallback] = images;
  }
  return nightGroups;
};

// Add this helper to generate a unique equipment signature
export function getEquipmentSignature(equipment: {
  telescopeName: string;
  focalLength: number;
  focalRatio: number;
  cameraName: string;
  pixelSize: number;
  bitDepth: number;
}): string {
  return [
    (equipment.telescopeName || "").toLowerCase().replace(/\s+/g, ""),
    equipment.focalLength,
    equipment.focalRatio,
    (equipment.cameraName || "").toLowerCase().replace(/\s+/g, ""),
    equipment.pixelSize,
    equipment.bitDepth,
  ].join("-");
}

/**
 * Merge two targets into one
 */
function mergeTargets(existing: TargetData, newTarget: TargetData): TargetData {
  // Merge images, update fields
  const existingPaths = new Set(existing.images.map((img) => img.FilePath));
  const mergedImages = [
    ...existing.images,
    ...newTarget.images.filter((img) => !existingPaths.has(img.FilePath)),
  ];

  // Merge nightMetadata: preserve existing, add new
  const mergedNightMetadata = { ...existing.nightMetadata };
  for (const night in newTarget.nightMetadata) {
    if (!mergedNightMetadata[night]) {
      mergedNightMetadata[night] = newTarget.nightMetadata[night];
    }
  }

  // Now build the merged target, using the existing target as the base
  return {
    ...existing,
    images: mergedImages,
    nightMetadata: mergedNightMetadata,
  };
}

export function handleImport({
  acquisitionFile,
  metadataFile,
  setError,
  setLoading,
  fetchTargets,
  setSelectedTarget,
  setAcquisitionFile,
  setMetadataFile,
  processTargetData,
  normalizeTargetName,
}: {
  acquisitionFile: File | null;
  metadataFile: File | null;
  setError: (msg: string | null) => void;
  setLoading: (loading: boolean) => void;
  fetchTargets: () => Promise<TargetData[]>;
  setSelectedTarget: (target: TargetData | null) => void;
  setAcquisitionFile: (file: File | null) => void;
  setMetadataFile: (file: File | null) => void;
  processTargetData: (
    acquisitionDetails: AcquisitionDetails,
    images: ImageMetadata[]
  ) => TargetData;
  normalizeTargetName: (name: string) => string;
}) {
  return async function () {
    if (!acquisitionFile || !metadataFile) {
      setError("Both acquisition and metadata files are required");
      return;
    }

    setLoading(true);

    try {
      const acquisitionText = await acquisitionFile.text();
      const metadataText = await metadataFile.text();

      const acquisitionData = JSON.parse(acquisitionText);
      const metadataData = JSON.parse(metadataText);

      const newTargetData = processTargetData(acquisitionData, metadataData);

      // Fetch current targets
      const currentTargets = await targetsApi.getAll();

      // Find existing target by normalized name AND equipment signature
      const normName = normalizeTargetName(newTargetData.name);
      const newEquipSig = getEquipmentSignature(newTargetData.equipment);
      const existing = currentTargets.find(
        (t: TargetData) =>
          normalizeTargetName(t.name) === normName &&
          t.equipment &&
          getEquipmentSignature(t.equipment) === newEquipSig
      );
      let updatedTargetId: string | null = null;

      if (existing) {
        // Merge with existing target
        console.log("Merging with existing target", existing.id);
        const updatedTarget = mergeTargets(existing, newTargetData);

        // Update the existing target
        await targetsApi.update(existing.id, updatedTarget);
        updatedTargetId = existing.id;
      } else {
        // Create new target
        console.log("Creating new target");
        const createdTarget = await targetsApi.create(newTargetData);
        updatedTargetId = createdTarget.id;
      }

      // Refresh targets and select the one we just created/updated
      const refreshedTargets = await fetchTargets();
      if (updatedTargetId) {
        const target = refreshedTargets.find(
          (t: TargetData) => t.id === updatedTargetId
        );
        if (target) {
          setSelectedTarget(target);
        }
      }

      // Clear the files
      setAcquisitionFile(null);
      setMetadataFile(null);
    } catch (err) {
      setError(
        "Error importing target: " +
          (err instanceof Error ? err.message : String(err))
      );
    } finally {
      setLoading(false);
    }
  };
}

export function fetchTargets({
  setTargets,
  setError,
  setLoading,
}: {
  setTargets: (targets: TargetData[]) => void;
  setError: (msg: string | null) => void;
  setLoading: (loading: boolean) => void;
}) {
  return async function () {
    try {
      console.log(
        "fetchTargets called, adding a small delay to ensure token is set"
      );

      // Set loading state to true at the beginning
      setLoading(true);

      // Add a small delay to ensure token is set in localStorage
      await new Promise((resolve) => setTimeout(resolve, 500));

      console.log("Delay complete, now fetching targets");
      const data = await targetsApi.getAll();

      // Ensure data is an array before setting targets
      const targetsArray = Array.isArray(data) ? data : [];
      console.log(`Received ${targetsArray.length} targets`);
      setTargets(targetsArray);

      // Set loading to false after successful fetch
      setLoading(false);

      return targetsArray;
    } catch (err) {
      console.error("Error fetching targets:", err);
      setError(
        "Error fetching targets: " +
          (err instanceof Error ? err.message : String(err))
      );
      setTargets([]); // Set empty array on error

      // Set loading to false on error
      setLoading(false);

      return [];
    }
  };
}

export function handleFileChange({
  setAcquisitionFile,
  setMetadataFile,
}: {
  setAcquisitionFile: (file: File | null) => void;
  setMetadataFile: (file: File | null) => void;
}) {
  return function (
    event: React.ChangeEvent<HTMLInputElement>,
    type: "acquisition" | "metadata"
  ) {
    const file = event.target.files?.[0];
    if (file) {
      if (type === "acquisition") {
        setAcquisitionFile(file);
      } else {
        setMetadataFile(file);
      }
    }
  };
}

export function handleDelete({
  selectedTarget,
  setSelectedTarget,
  setSelectedNight,
  setError,
  fetchTargets,
}: {
  selectedTarget: TargetData | null;
  setSelectedTarget: (target: TargetData | null) => void;
  setSelectedNight: (night: string | "total") => void;
  setError: (msg: string | null) => void;
  fetchTargets: () => Promise<TargetData[]>;
}) {
  return async function () {
    console.log("handleDelete called", { selectedTarget });
    if (!selectedTarget) {
      console.log("No selectedTarget, aborting delete");
      return;
    }

    try {
      // Delete the target
      console.log("Sending DELETE request for target", selectedTarget.id);
      await targetsApi.delete(selectedTarget.id);

      // Clear selected target and night
      setSelectedTarget(null);
      setSelectedNight("total");

      // Refresh the targets list
      await fetchTargets();
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Failed to delete target"
      );
    }
  };
}

// Add a new function to handle night deletion
export function handleDeleteNight({
  selectedTarget,
  setSelectedTarget,
  setSelectedNight,
  setError,
  fetchTargets,
}: {
  selectedTarget: TargetData | null;
  setSelectedTarget: (target: TargetData | null) => void;
  setSelectedNight: (night: string | "total") => void;
  setError: (msg: string | null) => void;
  fetchTargets: () => Promise<TargetData[]>;
}) {
  return async function (night: string) {
    if (!selectedTarget) return;

    try {
      // Remove all images for that night
      const nights = groupImagesByNight(selectedTarget.images);
      const imagesToKeep = Object.entries(nights)
        .filter(([date]) => date !== night)
        .flatMap(([, imgs]) => imgs);

      // If no images remain, delete the entire target
      if (imagesToKeep.length === 0) {
        const response = await targetsApi.delete(selectedTarget.id);

        if (!response.ok) {
          throw new Error("Failed to delete target");
        }

        // Clear selected target and night
        setSelectedTarget(null);
        setSelectedNight("total");
      } else {
        // Remove the night from nightMetadata
        const newNightMetadata = { ...selectedTarget.nightMetadata };
        delete newNightMetadata[night];

        // Recalculate totalExposureTime and filters
        const totalExposureTime = imagesToKeep.reduce(
          (sum, img) => sum + (img.Duration || 0),
          0
        );
        const filters = Array.from(
          new Set(imagesToKeep.map((img) => img.FilterName).filter(Boolean))
        );

        // Build updated target
        const updatedTarget = {
          ...selectedTarget,
          images: imagesToKeep,
          nightMetadata: newNightMetadata,
          totalExposureTime,
          filters,
        };

        // Update on server
        // const putResp = await targetsApi.update(selectedTarget.id, updatedTarget); // Commented out as the response is unused
        await targetsApi.update(selectedTarget.id, updatedTarget);

        // Update selected target with new data
        setSelectedTarget(updatedTarget);
        setSelectedNight("total");
      }

      // Refresh the targets list
      await fetchTargets();
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Failed to delete night"
      );
    }
  };
}

export function handleEdit({
  selectedTarget,
  setSelectedTarget,
  setEditDialogOpen,
  setError,
  fetchTargets,
  selectedNight,
}: {
  updatedTarget?: TargetData;
  selectedTarget: TargetData | null;
  setSelectedTarget: (target: TargetData | null) => void;
  setEditDialogOpen: (open: boolean) => void;
  setError: (msg: string | null) => void;
  fetchTargets: () => Promise<TargetData[]>;
  selectedNight?: string | null;
}) {
  return async function (updatedTarget: TargetData) {
    if (!selectedTarget) return;

    try {
      // If the name is being changed, check for collision
      const updatedName = updatedTarget.name;
      const normalizedNewName = normalizeTargetName(updatedName);
      const normalizedOldName = normalizeTargetName(selectedTarget.name);
      let merged = false;

      // If editing a specific night and renaming to an existing target
      if (
        selectedNight &&
        selectedNight !== "total" &&
        normalizedNewName !== normalizedOldName
      ) {
        // Fetch all targets to check for collision
        const allTargets = await targetsApi.getAll();
        const existing = allTargets.find(
          (t: TargetData) =>
            normalizeTargetName(t.name) === normalizedNewName &&
            t.id !== selectedTarget.id
        );
        if (existing) {
          // Move only the selected night and its images
          const nightToMove = selectedTarget.nightMetadata[selectedNight];
          if (!nightToMove) throw new Error("Night data not found");
          // Images for that night
          const imagesToMove = selectedTarget.images.filter((img: any) => {
            const match = img.FilePath.match(/DATE_(\d{4}-\d{2}-\d{2})/);
            return match && match[1] === selectedNight;
          });
          // Merge into existing target
          const existingPaths = new Set(
            existing.images.map((img: any) => img.FilePath)
          );
          const mergedImages = [
            ...existing.images,
            ...imagesToMove.filter(
              (img: any) => !existingPaths.has(img.FilePath)
            ),
          ];
          // Merge nightMetadata: preserve existing, add/move this night
          const mergedNightMetadata = { ...existing.nightMetadata };
          if (!mergedNightMetadata[selectedNight]) {
            mergedNightMetadata[selectedNight] = nightToMove;
          }
          // Recalculate totalExposureTime and filters
          const totalExposureTime = mergedImages.reduce(
            (sum: number, img: any) => sum + (img.Duration || 0),
            0
          );
          const filters = Array.from(
            new Set(
              mergedImages.map((img: any) => img.FilterName).filter(Boolean)
            )
          );
          // Build merged target
          const mergedTarget = {
            ...existing,
            images: mergedImages,
            nightMetadata: mergedNightMetadata,
            totalExposureTime,
            filters,
          };
          // Update existing target on server
          const putResp = await targetsApi.update(existing.id, mergedTarget);
          if (!putResp.ok) throw new Error("Failed to update (merge) target");

          // Remove the night and its images from the original target
          const newNightMetadata = { ...selectedTarget.nightMetadata };
          delete newNightMetadata[selectedNight];
          const imagesToKeep = selectedTarget.images.filter((img: any) => {
            const match = img.FilePath.match(/DATE_(\d{4}-\d{2}-\d{2})/);
            return !(match && match[1] === selectedNight);
          });
          if (Object.keys(newNightMetadata).length === 0) {
            // No nights left, delete the original target
            const delResp = await targetsApi.delete(selectedTarget.id);
            if (!delResp.ok)
              throw new Error("Failed to delete old target after moving night");
          } else {
            // Update the original target with remaining nights/images
            const totalExposureTime = imagesToKeep.reduce(
              (sum: number, img: any) => sum + (img.Duration || 0),
              0
            );
            const filters = Array.from(
              new Set(
                imagesToKeep.map((img: any) => img.FilterName).filter(Boolean)
              )
            );
            const updatedOriginal = {
              ...selectedTarget,
              images: imagesToKeep,
              nightMetadata: newNightMetadata,
              totalExposureTime,
              filters,
            };
            const putResp2 = await targetsApi.update(
              selectedTarget.id,
              updatedOriginal
            );
            if (!putResp2.ok)
              throw new Error(
                "Failed to update original target after moving night"
              );
          }
          // Fetch updated targets and set selected
          let updatedTargets = await fetchTargets();
          const updated = updatedTargets.find(
            (t: TargetData) => t.id === existing.id
          );
          if (updated) {
            setSelectedTarget(updated);
          }
          setEditDialogOpen(false);
          merged = true;

          // --- NEW: Remove this night from all other targets (except destination) ---
          // Fetch all targets again (in case of changes)
          const allTargetsAfter = await targetsApi.getAll();
          for (const t of allTargetsAfter) {
            if (t.id === existing.id) continue; // skip destination
            if (t.nightMetadata && t.nightMetadata[selectedNight]) {
              // Remove the night and its images
              const newNightMetadata = { ...t.nightMetadata };
              delete newNightMetadata[selectedNight];
              const imagesToKeep = t.images.filter((img: any) => {
                const match = img.FilePath.match(/DATE_(\d{4}-\d{2}-\d{2})/);
                return !(match && match[1] === selectedNight);
              });
              if (Object.keys(newNightMetadata).length === 0) {
                // No nights left, delete the target
                await targetsApi.delete(t.id);
              } else {
                // Update the target with remaining nights/images
                const totalExposureTime = imagesToKeep.reduce(
                  (sum: number, img: any) => sum + (img.Duration || 0),
                  0
                );
                const filters = Array.from(
                  new Set(
                    imagesToKeep
                      .map((img: any) => img.FilterName)
                      .filter(Boolean)
                  )
                );
                const updatedT = {
                  ...t,
                  images: imagesToKeep,
                  nightMetadata: newNightMetadata,
                  totalExposureTime,
                  filters,
                };
                await targetsApi.update(t.id, updatedT);
              }
            }
          }
          // Optionally, refresh targets again
          await fetchTargets();
        }
      }
      // If not a night move, keep the current logic for full-target rename/merge
      if (!merged) {
        // No collision or not a rename, proceed as usual
        const response = await targetsApi.update(
          selectedTarget.id,
          updatedTarget
        );

        if (!response.ok) {
          throw new Error("Failed to update target");
        }

        // Fetch updated targets
        const updatedTargets = await fetchTargets();

        // Find and set the updated target
        const updated = updatedTargets.find(
          (t: TargetData) => t.id === selectedTarget.id
        );
        if (updated) {
          setSelectedTarget(updated);
        }

        setEditDialogOpen(false);
      }
    } catch (err) {
      setError(
        "Error updating target: " +
          (err instanceof Error ? err.message : String(err))
      );
    }
  };
}

/**
 * Delete frames of a specific type from a target, optionally filtered by night.
 * @param target The target to update
 * @param frameType The frame type to delete ("LIGHT", "DARK", "FLAT", "BIAS")
 * @param night (optional) The night date (YYYY-MM-DD) to restrict deletion
 * @returns Updated TargetData with frames removed
 */
export function deleteFramesByType(
  target: TargetData,
  frameType: "LIGHT" | "DARK" | "FLAT" | "BIAS",
  night?: string
): TargetData {
  let filteredImages = target.images.filter((img) => {
    const matchesType = img.FrameType === frameType;
    let matchesNight = true;
    if (night) {
      // Extract night from FilePath (DATE_(\d{4}-\d{2}-\d{2}))
      const match = img.FilePath.match(/DATE_(\d{4}-\d{2}-\d{2})/);
      matchesNight = match ? match[1] === night : false;
    }
    return !(matchesType && matchesNight);
  });
  // Recalculate totalExposureTime and filters
  const totalExposureTime = filteredImages.reduce(
    (sum, img) => sum + (img.Duration || 0),
    0
  );
  const filters = Array.from(
    new Set(filteredImages.map((img) => img.FilterName).filter(Boolean))
  );
  return {
    ...target,
    images: filteredImages,
    totalExposureTime,
    filters,
  };
}

// Deep equality check for objects/arrays
export function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (typeof a !== "object" || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (!bKeys.includes(key)) return false;
    if (!deepEqual(a[key], b[key])) return false;
  }
  return true;
}

/**
 * Delete a target by its id directly, without relying on selectedTarget state.
 */
export async function deleteTargetById(
  id: string | string[],
  fetchTargets: () => Promise<TargetData[]>,
  setError: (msg: string | null) => void
) {
  try {
    if (Array.isArray(id)) {
      // Use batch delete for multiple IDs
      await targetsApi.batchDelete(id);
    } else {
      // Use single delete for one ID
      await targetsApi.delete(id);
    }
    await fetchTargets();
  } catch (error) {
    setError(
      error instanceof Error ? error.message : "Failed to delete target"
    );
  }
}
